<template>
  <div class="bg-white p-1 rounded-xl shadow-md">
    <!-- Year selector -->
    <div class="row mb-3">
      <div class="col-12 d-flex justify-content-end">
        <div class="col-3">
          <select v-model="year" class="form-select">
            <option v-for="y in years" :key="y" :value="y">
              {{ y }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Chart -->
    <div class="row apexchart_margin justify-content-center">
      <apexchart
        v-if="series.length"
        type="radialBar"
        height="260"
        :options="chartOptions"
        :series="series"
      />
    </div>

    <!-- Actual / Target -->
    <div class="text-center mt-3">
      <div class="text-sm text-gray-800 font-medium fw-bold mb-2 mt-4">
        {{ actualText }}
      </div>
      <div class="text-sm text-gray-500 font-medium fw-bold">
        {{ targetText }}
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";

export default {
  name: "YTDRevenueChart",
  components: {
    apexchart: VueApexCharts
  },

  data() {
    return {
      year: new Date().getFullYear()
      // years: [2022, 2023, 2024, 2025, 2026]
    };
  },

  computed: {
    years() {
      const startYear = 2022
      const currentYear = new Date().getFullYear()
      const result = []

      for (let y = startYear; y <= currentYear; y++) {
        result.push(y)
      }

      return result
    },
    percent() {
      return this.$store.getters["dashboard/ytdRevenuePercent"];
    },
    revenue() {
      return this.$store.getters["dashboard/ytdRevenueActual"];
    },
    target() {
      return this.$store.getters["dashboard/ytdRevenueTarget"];
    },

    /* ===============================
    * CHART SERIES
    * =============================== */
    series() {
      return this.percent !== null ? [this.percent] : [];
    },

    /* ===============================
    * COLOR LOGIC
    * =============================== */
    chartColor() {
      if (this.percent < 50) return "#EF4444";   // red
      if (this.percent < 80) return "#F59E0B";   // yellow
      return "#22C55E";                          // green
    },

    /* ===============================
    * CHART OPTIONS
    * =============================== */
    chartOptions() {
      return {
        chart: {
          type: "radialBar",
          height: 280
        },
        colors: ["#6366F1"],
        // colors: [this.chartColor],
        plotOptions: {
          radialBar: {
            size: 190,
            offsetY: 10,
            startAngle: -150,
            endAngle: 150,
            hollow: {
              size: "55%"
            },
            track: {
              background: "#EEF2FF",
              strokeWidth: "100%"
            },
            dataLabels: {
              name: {
                show: false   // ⬅️ remove "series-1"
              },
              value: {
                offsetY: 0,
                fontSize: "24px",
                fontWeight: 600,
                color: "#6366F1",
                formatter: v => `${Math.round(v)}%`
              }
            }
          }
        },
        fill: {
          type: "gradient",
          gradient: {
            shade: "dark",
            shadeIntensity: 0.5,
            gradientToColors: ["#6366F1"],
            // gradientToColors: [this.chartColor],
            inverseColors: true,
            opacityFrom: 1,
            opacityTo: 0.6,
            stops: [30, 70, 100]
          }
        },
        stroke: {
          dashArray: 5
        },
        grid: {
          padding: {
            top: -30,
            bottom: -10
          }
        }
      };
    },

    /* ===============================
    * DISPLAY TEXT
    * =============================== */
    // actualText() {
    //   if (!this.revenue) return "Actual: VND 0";
    //   return `Actual: VND ${(this.revenue / 1e9).toFixed(3)} billion`;
    // },
    // targetText() {
    //   if (!this.target) return "Target: VND 0";
    //   return `Target: VND ${(this.target / 1e9).toFixed(3)} billion`;
    // }
    actualText() {
      if (!this.revenue) return "Actual: 0 VND";

      const billion = this.revenue / 1e9;
      const fixed = billion.toFixed(3);

      // xxx.000 → xxx.0
      if (fixed.endsWith(".000")) {
        return `Actual: ${billion.toFixed(1)} B VND`;
      }

      // xxx.x00 → xxx.x
      if (/\.?\d0{2}$/.test(fixed)) {
        return `Actual: ${Number(fixed).toFixed(1)} B VND`;
      }

      // xxx.xy0 → xxx.xy
      if (fixed.endsWith("0")) {
        return `Actual: ${Number(fixed).toFixed(2)} B VND`;
      }

      return `Actual: ${fixed} B VND`;
    },

    targetText() {
      if (!this.target) return "Target: VND 0";

      const billion = this.target / 1e9;
      const fixed = billion.toFixed(3);

      if (fixed.endsWith(".000")) {
        return `Target: ${billion.toFixed(1)} B VND`;
      }

      if (/\.?\d0{2}$/.test(fixed)) {
        return `Target: ${Number(fixed).toFixed(1)} B VND`;
      }

      if (fixed.endsWith("0")) {
        return `Target: ${Number(fixed).toFixed(2)} B VND`;
      }

      return `Target: ${fixed} B VND`;
    }

  },

  watch: {
    year(newYear) {
      this.fetchData(newYear);
    }
  },

  methods: {
    fetchData(year) {
      this.$store.dispatch("dashboard/fetchYTDRevenueTarget", year);
    }
  },

  created() {
    this.fetchData(this.year);
  }
};
</script>

<style scoped>
  .apexchart_margin {
    margin-top: 5rem;
  }
  .cost-card {
    max-width: 340px;
    padding: 20px;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .card-header h3 {
    font-size: 18px;
    font-weight: 600;
    color: #374151;
  }

  .year-select {
    padding: 6px 14px;
    border-radius: 12px;
    border: none;
    background: #EEF2FF;
    color: #4F46E5;
    font-weight: 600;
    outline: none;
  }
  .apexchart_margin {
    margin-top: 3rem;
  }
</style>